import { useState } from "react";
import toast from "react-hot-toast";
import {
  getEsicReport,
  getFinancialReport,
  getFormB,
  getFormD,
  getPfEcr,
} from "../../../Services/payrollServices";
import { saveExcelBlob } from "../../../Components/Common/DownloadExcelBill";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";

// Template column -> key in the /form-b/ API row
const FORM_B_COLUMNS = {
  A: "sr_no",
  B: "employee_register_no",
  C: "name",
  D: "ep_number",
  E: "uan_number",
  F: "esic_number",
  G: "trade",
  H: "skill_category",
  I: "rate_of_wage",
  J: "days_worked",
  K: "week_off",
  L: "total_days",
  M: "overtime_hours",
  // Wages P.M.
  N: "pm_basic",
  O: "pm_da",
  P: "pm_basic_da_total",
  Q: "pm_hra",
  R: "pm_special_allowance",
  S: "pm_travel_allowance",
  T: "pm_conveyance",
  U: "pm_medical",
  V: "pm_attire",
  W: "pm_travel_reimbursement",
  X: "pm_bonus",
  Y: "pm_leave",
  Z: "pm_gross",
  // Earned wages
  AA: "earned_basic",
  AB: "earned_da",
  AC: "earned_basic_da_total",
  AD: "earned_overtime",
  AE: "earned_hra",
  AF: "earned_special_allowance",
  AG: "earned_travel_allowance",
  AH: "earned_conveyance",
  AI: "earned_medical",
  AJ: "earned_attire",
  AK: "earned_travel_reimbursement",
  AL: "earned_bonus",
  AM: "earned_leave",
  AN: "earned_gross",
  // Deductions
  AO: "ded_pf",
  AP: "ded_esic",
  AQ: "ded_p_tax",
  AR: "ded_mlwb",
  AS: "ded_income_tax",
  AT: "ded_insurance",
  AU: "ded_others",
  AV: "ded_recoveries",
  AW: "ded_total",
  AX: "net_payment",
  // Employer share
  AY: "employer_pf",
  AZ: "employer_esic",
  BA: "employer_mlwb",
  BB: "bank_account",
  BC: "payment_date",
  BD: "utr_number",
  BE: "remarks",
};

const columnsBetween = (from, to) => {
  const keys = Object.keys(FORM_B_COLUMNS);
  return keys.slice(keys.indexOf(from), keys.indexOf(to) + 1);
};

// Extra column width (in characters) around a Grand Total figure
const TOTAL_WIDTH_PADDING = 2;

const cellValue = (value) => (value === "" || value == null ? null : value);

const firstRowOfRange = (range) => Number(range.match(/\d+/)[0]);

// Form D: day 1 is column E, 31 day columns up to AI
const FORM_D_FIRST_DAY_COLUMN = 5;
const FORM_D_DAY_COLUMNS = 31;

const REGISTERS = {
  formB: {
    label: "Form B",
    fileName: "Form_B",
    template: "/excel/form-b-template.xlsx",
    fetch: getFormB,
    firstRow: 13,
    // Blank employee rows in the template; the Grand Total row sits right below them
    templateRows: 5,
    // Grand Total sums "No. of Days Worked" .. "Employer MLWB"
    totalColumns: columnsBetween("J", "BA"),
    // Used to widen narrow template columns (e.g. P, AA, AC) whose total would show as ####
    columnTotal: (rows, column) =>
      rows.reduce((sum, row) => sum + (Number(row[FORM_B_COLUMNS[column]]) || 0), 0),
    // Signature block, 7 rows below Grand Total in the reference register
    signatureColumn: "I",
    signatureOffset: 7,
    fillHeader: (worksheet, { establishment, wage_period: period }) => {
      worksheet.getCell("A8").value = `Name of Establishment :- ${establishment.name}`;
      worksheet.getCell("L8").value = `Name of owner :- ${establishment.owner_name}`;
      worksheet.getCell("AB8").value = `LIN :- ${establishment.lin}`;
      worksheet.getCell("AM8").value = `Wage Period From : ${period.from} To ${period.to}`;
      worksheet.getCell("A9").value = `Address of Establishment :- ${establishment.address}`;
    },
    fillRow: (excelRow, row) => {
      Object.entries(FORM_B_COLUMNS).forEach(([column, key]) => {
        excelRow.getCell(column).value = cellValue(row[key]);
      });
    },
  },
  formD: {
    label: "Form D",
    fileName: "Form_D",
    template: "/excel/form-d-template.xlsx",
    fetch: getFormD,
    firstRow: 8,
    templateRows: 5,
    totalColumns: ["AJ", "AK", "AL"],
    signatureColumn: "H",
    signatureOffset: 8,
    fillHeader: (worksheet, { establishment, wage_period: period, rows }) => {
      const toDotted = (value) => value.replace(/-/g, ".");
      worksheet.getCell("A2").value = `Name of Establishment : ${establishment.name}`;
      worksheet.getCell("M2").value = `Name of Owner : ${establishment.owner_name}`;
      worksheet.getCell("AM2").value = `LIN: ${establishment.lin}`;
      worksheet.getCell("A3").value = `Address of Establishment :- ${establishment.address}`;
      worksheet.getCell("A4").value =
        `For the Period From ${toDotted(period.from)} To ${toDotted(period.to)}`;

      // Day numbers; columns past the month's last day stay empty
      const daysInMonth = rows[0].days.length;
      const dayRow = worksheet.getRow(7);
      for (let day = 1; day <= FORM_D_DAY_COLUMNS; day++) {
        dayRow.getCell(FORM_D_FIRST_DAY_COLUMN + day - 1).value =
          day <= daysInMonth ? day : null;
      }
    },
    fillRow: (excelRow, row) => {
      const r = excelRow.number;
      excelRow.getCell("A").value = row.sr_no;
      excelRow.getCell("B").value = row.name;
      excelRow.getCell("C").value = cellValue(row.relay);
      excelRow.getCell("D").value = cellValue(row.place_of_work);
      for (let day = 0; day < FORM_D_DAY_COLUMNS; day++) {
        excelRow.getCell(FORM_D_FIRST_DAY_COLUMN + day).value = cellValue(row.days[day]);
      }
      // Same formulas as the reference register
      const days = `E${r}:AI${r}`;
      excelRow.getCell("AJ").value = {
        formula: `COUNTIF(${days},"P")+COUNTIF(${days},"P/2")/2+COUNTIF(${days},"PH")`,
      };
      excelRow.getCell("AK").value = { formula: `COUNTIF(${days},"WO")` };
      excelRow.getCell("AL").value = { formula: `AJ${r}+AK${r}` };
      excelRow.getCell("AM").value = cellValue(row.overtime_hours);
    },
  },
  financialReport: {
    label: "Financial Report",
    fileName: "Financial_Report",
    template: "/excel/financial-report-template.xlsx",
    fetch: getFinancialReport,
    firstRow: 4,
    templateRows: 5,
    fill: (worksheet, config, data) => fillFinancialReport(worksheet, config, data),
  },
  esicReport: {
    label: "ESIC",
    fileName: "ESIC",
    template: "/excel/esic-template.xlsx",
    fetch: getEsicReport,
    firstRow: 2,
    fill: (worksheet, config, data) => fillEsicReport(worksheet, config, data),
    emptyMessage: "No ESIC-covered employees in the salary slips of",
  },
  pfEcr: {
    label: "PF ECR",
    fetch: getPfEcr,
    // EPFO ECR text upload: UTF-8 with BOM, CRLF lines, fields joined by #~#
    buildFile: ({ rows }) =>
      new Blob(["\uFEFF" + rows.map((fields) => fields.join("#~#")).join("\r\n")], {
        type: "text/plain;charset=utf-8",
      }),
    // e.g. OCT2026.txt, like the files uploaded to EPFO
    getFileName: (data) => `${data.file_name}.txt`,
    emptyMessage: "No PF members in the salary slips of",
  },
};

// ESIC upload columns, in the portal's order
const ESIC_COLUMNS = ["ip_number", "ip_name", "days_paid", "wages", "zero_days_reason", "last_working_day"];

const fillEsicReport = (worksheet, { firstRow }, { rows }) => {
  const styleRow = worksheet.getRow(firstRow);
  rows.forEach((row, index) => {
    const excelRow = worksheet.getRow(firstRow + index);
    ESIC_COLUMNS.forEach((key, i) => {
      const cell = excelRow.getCell(i + 1);
      cell.style = JSON.parse(JSON.stringify(styleRow.getCell(i + 1).style));
      cell.value = cellValue(row[key]);
    });
  });
};

// Financial report: sheet column -> key in the /financial-report/ API row.
// Net Salary (D) and Employer PF (H) are formulas, as in the accounts sheet.
const FINANCIAL_REPORT_COLUMNS = {
  A: "name",
  B: "gross",
  C: "tds",
  E: "employee_pf",
  F: "pension",
  G: "epf",
  I: "employee_mlwf",
  J: "esic",
  K: "employer_esic",
  L: "pt",
  M: "employer_mlwf",
  N: "edli",
  O: "pf_admin",
  P: "reimbursement",
};
const FINANCIAL_REPORT_TOTAL_COLUMNS = "BCDEFGHIJKLMNOP".split("");
// "Payable" repeats the totals, except PF admin charges which have a monthly minimum
const FINANCIAL_REPORT_PAYABLE_COLUMNS = "BCDEFGHIJKLMN".split("");

const fillFinancialReport = (worksheet, { firstRow, templateRows }, { establishment, month, rows }) => {
  worksheet.getCell("A1").value =
    `${establishment.name} Employee Salary sheet Month Of ${month}`.trim();

  const extraRows = rows.length - templateRows;
  if (extraRows > 0) {
    worksheet.duplicateRow(firstRow + templateRows - 1, extraRows, true);
  }

  rows.forEach((row, index) => {
    const excelRow = worksheet.getRow(firstRow + index);
    const r = excelRow.number;
    Object.entries(FINANCIAL_REPORT_COLUMNS).forEach(([column, key]) => {
      // Zero amounts show as "-" through the template's number formats
      excelRow.getCell(column).value = cellValue(row[key]);
    });
    excelRow.getCell("D").value = { formula: `B${r}-(C${r}+E${r}+SUM(H${r}:M${r}))+P${r}` };
    excelRow.getCell("H").value = { formula: `F${r}+G${r}` };
  });

  const lastDataRow = firstRow + Math.max(rows.length, templateRows) - 1;
  const totalRow = lastDataRow + 1;
  const minimumRow = totalRow + 1;
  const payableRow = totalRow + 2;
  FINANCIAL_REPORT_TOTAL_COLUMNS.forEach((column) => {
    worksheet.getCell(`${column}${totalRow}`).value = {
      formula: `SUM(${column}${firstRow}:${column}${lastDataRow})`,
    };
  });
  FINANCIAL_REPORT_PAYABLE_COLUMNS.forEach((column) => {
    worksheet.getCell(`${column}${payableRow}`).value = { formula: `${column}${totalRow}` };
  });
  worksheet.getCell(`O${payableRow}`).value = {
    formula: `MAX(O${totalRow},O${minimumRow})`,
  };
};

const fillRegister = (worksheet, config, data) => {
  const { firstRow, templateRows } = config;
  const { rows, signatory } = data;

  // exceljs writes these template features as XML that older Excel (2007) refuses to
  // open: "duplicate values" highlight rules come out as empty conditionalFormatting
  // blocks, and outline settings land after pageSetUpPr in <sheetPr>. Neither is
  // needed in a generated register (the outline values are Excel's defaults).
  worksheet.removeConditionalFormatting();
  delete worksheet.properties.outlineProperties;

  // Leftover merges in the data/total rows (e.g. Form B N13:P13) would swallow
  // separate values such as Basic / DA / Total. A merged range keeps its border and
  // fill on the first cell only, so give every cell of the range that styling.
  worksheet.model.merges
    .filter((range) => firstRowOfRange(range) >= firstRow)
    .forEach((range) => {
      const [first, last] = range.split(":");
      const style = JSON.parse(JSON.stringify(worksheet.getCell(first).style));
      if (style.border?.left && !style.border.right) {
        style.border.right = style.border.left;
      }
      worksheet.unMergeCells(range);
      const from = worksheet.getCell(first);
      const to = worksheet.getCell(last);
      for (let row = from.row; row <= to.row; row++) {
        for (let col = from.col; col <= to.col; col++) {
          worksheet.getCell(row, col).style = JSON.parse(JSON.stringify(style));
        }
      }
    });

  // Copy the last blank row's formatting for employees beyond the template's rows
  const lastTemplateRow = firstRow + templateRows - 1;
  const extraRows = rows.length - templateRows;
  if (extraRows > 0) {
    worksheet.duplicateRow(lastTemplateRow, extraRows, true);
  }

  rows.forEach((row, index) => config.fillRow(worksheet.getRow(firstRow + index), row));

  const lastDataRow = firstRow + Math.max(rows.length, templateRows) - 1;
  const totalRowNumber = lastDataRow + 1;
  const totalRow = worksheet.getRow(totalRowNumber);
  config.totalColumns.forEach((column) => {
    totalRow.getCell(column).value = {
      formula: `SUM(${column}${firstRow}:${column}${lastDataRow})`,
    };
    if (config.columnTotal) {
      const digits = String(Math.round(config.columnTotal(rows, column))).length;
      const sheetColumn = worksheet.getColumn(column);
      sheetColumn.width = Math.max(sheetColumn.width || 0, digits + TOTAL_WIDTH_PADDING);
    }
  });

  // Nothing but the signature belongs below the totals (the Form B template has
  // a stray "=+W19/30" there)
  for (let r = totalRowNumber + 1; r <= worksheet.rowCount; r++) {
    worksheet.getRow(r).eachCell((cell) => {
      if (cell.formula) cell.value = null;
    });
  }

  const signatureLines = [
    signatory.name,
    signatory.designation,
    `DIN: ${signatory.din}`,
    `Date: ${signatory.date}`,
    `Place: ${signatory.place}`,
  ];
  signatureLines.forEach((line, index) => {
    const r = totalRowNumber + config.signatureOffset + index;
    worksheet.getCell(`${config.signatureColumn}${r}`).value = cellValue(line);
  });

  config.fillHeader(worksheet, data);
};

export const useIRCompliance = () => {
  // Which report is being generated (a REGISTERS key), null when idle
  const [downloading, setDownloading] = useState(null);

  const downloadRegister = async (type, { month, company }) => {
    const config = REGISTERS[type];
    setDownloading(type);
    try {
      const response = await config.fetch({ month, company });
      if (!response.data?.rows?.length) {
        toast.error(
          config.emptyMessage
            ? `${config.emptyMessage} ${month}.`
            : `No salary slips found for ${month}. Run payroll or upload salary slips for this month first.`
        );
        return;
      }

      if (config.buildFile) {
        await saveExcelBlob(config.buildFile(response.data), config.getFileName(response.data));
        return;
      }

      // exceljs keeps the template's formatting (the xlsx package drops styles on write);
      // loaded on demand because it is a large bundle
      const { default: ExcelJS } = await import("exceljs");
      const template = await fetch(config.template);
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(await template.arrayBuffer());

      (config.fill || fillRegister)(workbook.worksheets[0], config, response.data);
      workbook.calcProperties.fullCalcOnLoad = true;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      await saveExcelBlob(blob, `${config.fileName}_${company || "All"}_${month}.xlsx`);
    } catch (error) {
      toast.error(extractErrorMessage(error) || `Failed to download ${config.label}.`);
    } finally {
      setDownloading(null);
    }
  };

  return { downloading, downloadRegister };
};
